import { Box, useTheme } from "@mui/material";
import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import { getConfirmButtons } from "../../data/custom-dialog-templates";
import { DataGridText, DialogText, UploadedFilesText } from "../../data/texts";

import { ActionsRowCta, FileRow, FileWithId } from "../../models/uploaded-file";
import { DateTimeService } from "../../services/date-time.service";
import {
  FileService,
  FileShortType,
  FileTypes,
} from "../../services/file.service";
import ConfirmationDialog from "../common/CustomDialog";
import CustomTable, { PaginationModel } from "../common/CustomTable";
import ActionsRow from "./custom-rows/ActionsRow";
import FileTypeRow from "./custom-rows/FileTypeRow";
import CustomFooter, { CustomFooterProps } from "./CustomFooter";
import { DialogCta } from "../../models/custom-dialog.models";
import { UtilsService } from "../../services/utils.service";

interface UploadedFilesTableProps {
  filesWithId: FileWithId[];
  onDeleteFile: (file: FileWithId) => void;
  onDeleteMultipleFiles: (files: FileWithId[]) => void;
  totalRowNumber: number;
  onPaginationModelChange?: (model: PaginationModel) => void;
  isLoading?: boolean;
  onActionClick?: (cta: ActionsRowCta, file: FileWithId) => void;
  stopLoading: VoidFunction;
}

const UploadedFilesTable = ({
  filesWithId,
  onDeleteFile,
  onDeleteMultipleFiles,
  onActionClick,
  totalRowNumber,
  stopLoading,
  isLoading,
  onPaginationModelChange,
}: UploadedFilesTableProps) => {
  const [tableRows, setTableRows] = useState<FileRow[]>();
  const theme = useTheme();
  const [fileToDelete, setFileToDelete] = useState<
    FileWithId | FileWithId[] | null
  >(null);
  const [selectedRows, setSelectedRows] = useState<FileWithId[] | null>();

  const onDeleteDialogButton = (cta: DialogCta) => {
    if (!fileToDelete) return;
    if (cta === DialogCta.CONFIRM) {
      if (Array.isArray(fileToDelete)) {
        onDeleteMultipleFiles(fileToDelete);
      } else {
        onDeleteFile(fileToDelete);
      }
    }
    setFileToDelete(null);
  };

  useEffect(() => {
    convertFileListToRows(filesWithId);
  }, [filesWithId]);

  const convertFileListToRows = (filesWithId: FileWithId[]) => {
    const rows: FileRow[] = filesWithId.map((file) => {
      const createdAt = DateTimeService.convertDateToDDMMYYYY(
        new Date(file.createdAt)
      );

      const shortenType = FileService.getFileTypeShortName(
        file.content?.type || ""
      );

      return {
        id: UtilsService.generateUniqueId(4),
        fileName: file.content?.name || "",
        size: `${FileService.convertBytesToHumanReadable(
          file.content?.size || 0
        )}`,
        createdAt,
        fileType: shortenType || FileShortType.ELSE,
        file,
      };
    });
    setTableRows(rows);
  };

  const tableColumns: GridColDef[] = [
    {
      field: "id",
      headerName: "ID",
      minWidth: 90,
      flex: 0.5,
      cellClassName: "id-column",
    },
    {
      field: "fileName",
      headerName: "File Name",
      minWidth: 150,
      flex: 2,
      cellClassName: "file-name-column",
    },
    { field: "size", headerName: "Size", minWidth: 100, flex: 1 },
    {
      field: "fileType",
      headerName: "File Type",
      minWidth: 120,
      flex: 1,
      renderCell: (param) => <FileTypeRow fileType={param.row.fileType} />,
      cellClassName: "file-type-column",
    },
    { field: "createdAt", headerName: "Created At", minWidth: 120, flex: 1 },
    {
      headerName: "Actions",
      field: "actions",
      minWidth: 110,
      flex: 1,
      renderCell: (params) => (
        <ActionsRow
          isPreviewDisabled={
            params.row.fileType === FileTypes.ZIP ||
            params.row.fileType === FileShortType.ELSE
          }
          onAction={(cta) => onAction(cta, params.row.file)}
        />
      ),
    },
  ];

  const onSelectRow = (selectedRowId: GridRowSelectionModel) => {
    const selectedRowsFiles = filesWithId.filter((file) =>
      selectedRowId.includes(file.id)
    );
    setSelectedRows(selectedRowsFiles);
  };

  const onAction = async (cta: ActionsRowCta, file: FileWithId) => {
    if (onActionClick) {
      onActionClick(cta, file);
    }

    const files = selectedRows?.length ? selectedRows : file;
    switch (cta) {
      case ActionsRowCta.DELETE:
        setFileToDelete(files);
        break;
      case ActionsRowCta.VIEW:
        await FileService.handleCrmFileAction(file, "preview");
        break;
      case ActionsRowCta.DOWNLOAD: {
        if (Array.isArray(files)) {
          files.forEach(
            async (file) =>
              await FileService.handleCrmFileAction(file, "download")
          );
        } else {
          await FileService.handleCrmFileAction(file, "download");
        }
      }
    }

    if (stopLoading) {
      stopLoading();
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        boxShadow: "0px 2px 10px 0px #4C4E6438",
        position: "relative",
        zIndex: "1",
        ...(isLoading && theme.customStyles.disabled),
      }}
    >
      {tableRows && (
        <CustomTable
          checkboxSelection
          onPaginationModelChange={onPaginationModelChange}
          totalRowNumber={totalRowNumber}
          slotProps={{
            footer: {
              selectedRowCount: selectedRows?.length,
              onAction,
            } as CustomFooterProps,
          }}
          extraDataGridSx={{
            "& .file-name-column": {
              fontWeight: "400 !important",
              color: "black",
            },
          }}
          localeText={isLoading ? "" : DataGridText.NO_FILES}
          slots={{ footer: CustomFooter }}
          onSelectRow={onSelectRow}
          title={UploadedFilesText.UPLOADED_FILES}
          tableRows={tableRows}
          tableColumns={tableColumns}
        />
      )}
      <ConfirmationDialog
        isOpen={!!fileToDelete}
        onButton={onDeleteDialogButton}
        title={DialogText.DELETE_FILE_TITLE}
        text={DialogText.DELETE_FILE_TEXT}
        buttons={getConfirmButtons()}
      />
    </Box>
  );
};

export default UploadedFilesTable;
